import { useEffect, useRef, useState } from "react";
import { pickPalette } from "@picnic-field/renderer/appearance";
import type { Appearance, PaletteLike } from "@picnic-field/renderer/appearance";
import { useBootDeps } from "../../../lib/boot/deps";
import { ModelPreview } from "../../../components/ModelPreview";
import type { PreviewFactory } from "../../../lib/preview";

/**
 * 작품 카드 썸네일(FS-11-PH1 · FS-11-06) — C-02 createPreview(BootDeps 공장 그대로)로 **보일 때 한 번** 그려
 * 그림(PNG)으로 떠 두고 곧바로 해제한다. 카드가 27개여도 WebGL 문맥은 동시에 둘까지만 쓴다(브라우저 문맥 한도 ≈16 —
 * 카드마다 캔버스를 띄우면 오래된 문맥부터 잃는다). 같은 모형·색은 한 번만 그린다(세션 캐시).
 * 돌려 보기(카드 열기 상세)는 ModelPreview(interactive)를 그대로 쓴다.
 */
type Look = { glbUrl: string | null; appearance: Appearance | null };

const W = 280;
const H = 128;
const cache = new Map<string, Promise<string | null>>();
let running = 0;
const waiting: (() => void)[] = [];

async function slot<T>(fn: () => Promise<T>): Promise<T> {
  if (running >= 2) await new Promise<void>((r) => waiting.push(r));
  running += 1;
  try {
    return await fn();
  } finally {
    running -= 1;
    waiting.shift()?.();
  }
}

/** 한 번 그려서 dataURL — 실패하면 null(자리표시) */
export function renderThumb(
  factory: PreviewFactory,
  look: Look,
  palette: PaletteLike | null,
): Promise<string | null> {
  // jsdom(단위 시험)에는 캔버스 그림 뜨기가 없다 — 공장은 부르되 그림은 자리표시로
  if (!look.glbUrl) return Promise.resolve(null);
  const noPixels = typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent);
  // 작품 제 팔레트 이름으로(2026-09-30 팔레트 버전 전환) — 묶음이면 그 이름의 것
  const pal = pickPalette(palette, look.appearance?.palette ?? null);
  const key = JSON.stringify([
    look.glbUrl,
    look.appearance,
    pal?.palette ?? null,
    pal?.colors.length ?? 0,
  ]);
  const hit = cache.get(key);
  if (hit) return hit;
  const job = slot(async () => {
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const h = await factory(canvas, {
      glbUrl: look.glbUrl!,
      appearance: look.appearance,
      palette: pal,
      interactive: false,
      autoRotate: false,
      quality: "low",
    });
    try {
      await h.ready;
      // 첫 프레임은 ready 가 풀리는 같은 작업 안에서 그려졌다 — 화면에 합성되기 전이라 버퍼가 남아 있다
      if (noPixels) return null;
      const url = canvas.toDataURL("image/png");
      return typeof url === "string" && url.startsWith("data:image/") ? url : null;
    } finally {
      h.dispose();
    }
  }).catch(() => null);
  cache.set(key, job);
  return job;
}

export function WorkThumb({
  work,
  palette,
  label,
  className = "pf-tthumb",
  interactive = false,
}: {
  work: Look;
  palette: PaletteLike | null;
  label: string;
  className?: string;
  interactive?: boolean;
}) {
  const { createPreview } = useBootDeps();
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === "undefined");
  const [url, setUrl] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    const el = ref.current;
    if (interactive || seen || !el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin: "120px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [interactive, seen]);

  useEffect(() => {
    if (interactive || !seen) return;
    let off = false;
    void renderThumb(createPreview, work, palette).then((u) => !off && setUrl(u));
    return () => {
      off = true;
    };
  }, [interactive, seen, createPreview, work, palette]);

  if (interactive)
    return (
      <ModelPreview
        glbUrl={work.glbUrl}
        appearance={work.appearance}
        palette={palette}
        label={label}
        interactive
        autoRotate={false}
        quality="low"
        className={className}
      />
    );
  return (
    <div
      ref={ref}
      className={`pf-tthumb-box ${url ? "pf-tthumb-box--img" : "pf-tthumb--idle"}`}
      data-testid="work-thumb"
      data-state={url ? "ready" : url === null ? "fallback" : "loading"}
      role="img"
      aria-label={label}
    >
      {url && <img src={url} alt="" />}
      {url === null && (
        <span className="pf-preview__glyph" aria-hidden="true">
          🧩
        </span>
      )}
    </div>
  );
}
